// GroupFaces demo film (landing-page hero), CAPTURED version: the real stage app recorded at 2x with Playwright
// (work/capture-*), each recording played back as a texture on the same tilted planes, depth of field, timing and
// ending as the recreated version (motion-recriado). Only the GSDG demo project appears on screen.
// `locale` picks the clip set (pt: work/capture-*, en: work/capture-*-en, the English project gf_uaymguiq) and the lines.
import React from 'react';
import {AbsoluteFill, Audio, Img, OffthreadVideo, interpolate, staticFile} from 'remotion';
import {Fonts} from './Fonts';
import {C, F, FPS, ease, easeOut} from './theme';
import {useT} from './dataCaptura';
import {STRINGS, type Locale} from './i18n';
import {Lens, Post, Shot} from './lens';
import {ramp} from './motion/util';

export const DURATION = 52;
const BG = '#030304';
const H = {a: 180, c: 0.5, w: 0.1, f: 0.2};  // horizontal focus band through the frame centre
// Every capture is a 1920x1080 CSS page recorded at 3840x2160: plane coordinates below are CSS pixels.
const W = 1920, HH = 1080;

// A recording on the plane: `from` = second of the clip at the shot's first frame, `rate` = playback speed.
type ClipName = 'wizard' | 'project' | 'extra';
const CLIP_SUFFIX: Record<Locale, string> = {pt: '', en: '-en'};
// Where each wizard beat starts in its recording: the PT wizard was recorded from step 3 (--from 10), the EN one from
// the start, so the same beats sit later in the EN clip. Project and extra clips share the PT timing.
const WIZARD_AT: Record<Locale, {targets: number; folders: number}> = {pt: {targets: 0.2, folders: 16.5}, en: {targets: 11.37, folders: 27.4}};
const Clip: React.FC<{name: ClipName; locale: Locale; from: number; rate?: number}> = ({name, locale, from, rate = 1}) =>
 <div style={{width: W, height: HH, borderRadius: 18, overflow: 'hidden', background: C.bg,
  boxShadow: '0 0 0 1px rgba(255,255,255,.07), 0 40px 120px rgba(0,0,0,.6)'}}>
  <OffthreadVideo src={staticFile(`clips/${name}${CLIP_SUFFIX[locale]}.mp4`)} trimBefore={Math.round(from * FPS)} playbackRate={rate} muted
   style={{width: W, height: HH, display: 'block'}}/>
 </div>;

// A short line on black: rises out of a blur, holds, leaves.
const Line: React.FC<{parts: [string, string][]; a: number; b: number}> = ({parts, a, b}) => {
 const t = useT();
 const k = Math.min(ramp(t, a, 0.9, easeOut), 1 - ramp(t, b - 0.5, 0.5, ease));
 if (k <= 0) return null;
 return <AbsoluteFill style={{alignItems: 'center', justifyContent: 'center'}}>
  <div style={{fontFamily: F.sans, fontSize: 68, fontWeight: 500, letterSpacing: '-.02em', whiteSpace: 'nowrap', opacity: k,
   filter: `blur(${((1 - k) * 12).toFixed(2)}px)`, transform: `translateY(${((1 - k) * 14).toFixed(2)}px) scale(${(1.0 + 0.012 * ramp(t, a, b - a)).toFixed(4)})`}}>
   {parts.map(([s, color], i) => <span key={i} style={{color}}>{s}</span>)}
  </div>
 </AbsoluteFill>;
};

// Closing mark: the GroupFaces logo settles and the address rises behind it.
const Mark: React.FC<{a: number}> = ({a}) => {
 const t = useT();
 const k = ramp(t, a, 0.9, easeOut), k2 = ramp(t, a + 0.35, 0.7, easeOut);
 if (k <= 0) return null;
 return <AbsoluteFill style={{alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 34}}>
  <Img src={staticFile('brand/logo.svg')} style={{height: 88, width: 'auto', opacity: k, filter: `blur(${((1 - k) * 10).toFixed(2)}px)`, transform: `scale(${(0.94 + 0.06 * k).toFixed(4)})`}}/>
  <div style={{fontFamily: F.mono, fontSize: 22, letterSpacing: '.26em', color: C.ink2, opacity: k2, transform: `translateY(${((1 - k2) * 10).toFixed(2)}px)`}}>groupfaces.io</div>
 </AbsoluteFill>;
};

// Landmarks on the captured pages (CSS px), measured on the recordings.
const NAV_PROJETOS = {x: 120, y: 170};
const TARGET_GRID = {x: 960, y0: 600, y1: 860};
const GALLERIES = {x0: 760, y0: 600, x1: 1000, y1: 820};
const METRIC_CLIENTS = {x: 1286, y: 244};
const CLIENT_ROWS = {x0: 440, y0: 270, x1: 1200, y1: 440};
const SEND_ROW = {x: 1617, y: 328};      // "Enviar link" on Mariana Albuquerque's row
const PUBLIC_TITLE = {x: 720, y: 180};

export type FilmCapturaProps = {locale: Locale};
export const Film: React.FC<FilmCapturaProps> = ({locale}) => {
 const s = STRINGS[locale], w = WIZARD_AT[locale];
 return <AbsoluteFill style={{background: BG}}>
 <Fonts>
  {/* 1 · sidebar, low angle, "Projetos" lights up under the cursor */}
  <Shot from={0} to={5.5} inn={0.8}>
   <Lens W={W} H={HH} cam={[{px: NAV_PROJETOS.x, py: NAV_PROJETOS.y, sx: 820, sy: 520, rx: 42, ry: -30, rz: 9, z: 520}, {px: NAV_PROJETOS.x, py: NAV_PROJETOS.y, sx: 820, sy: 520, rx: 32, ry: -17, rz: 5, z: 900}]}
    focus={[{...H, c: 0.48, w: 0.05, f: 0.17}, {...H, c: 0.48, w: 0.06, f: 0.17}]}><Clip name="extra" locale={locale} from={0.5}/></Lens>
  </Shot>
  {/* 2 · wizard step 3: reference photos drop in, "1 rosto" badges; tilt down across the cards */}
  <Shot from={5.0} to={11.5}>
   <Lens W={W} H={HH} cam={[{px: TARGET_GRID.x, py: TARGET_GRID.y0, rx: 30, ry: -5, z: 420}, {px: TARGET_GRID.x, py: TARGET_GRID.y1, rx: 19, ry: 3, z: 520}]}
    focus={[{...H, w: 0.12}, {...H, w: 0.12}]}><Clip name="wizard" locale={locale} from={w.targets} rate={1.9}/></Lens>
  </Shot>
  {/* 3 · wizard step 4: one folder per event becomes a gallery, the photo count climbs */}
  <Shot from={11.0} to={17.4}>
   <Lens W={W} H={HH} cam={[{px: GALLERIES.x0, py: GALLERIES.y0, rx: 10, ry: 27, z: 220}, {px: GALLERIES.x1, py: GALLERIES.y1, rx: 10, ry: 19, z: 300}]}
    focus={[{a: 90, c: 0.5, w: 0.16, f: 0.22}, {a: 90, c: 0.52, w: 0.17, f: 0.22}]}><Clip name="wizard" locale={locale} from={w.folders} rate={2}/></Lens>
  </Shot>
  {/* 4 · line on black */}
  <Shot from={17.0} to={22.3} inn={0.3} out={0.3}>
   <Line a={0.45} b={5.0} parts={[[s.line1[0], C.muted], [s.line1[1], C.ink]]}/>
  </Shot>
  {/* 5 · project overview loads, then push in on "Clientes identificados" */}
  <Shot from={21.9} to={30.5}>
   <Lens W={W} H={HH} cam={[{px: 1000, py: 380, rx: 26, ry: -14, rz: 6, z: -280}, {px: METRIC_CLIENTS.x, py: METRIC_CLIENTS.y, rx: 17, ry: -22, rz: 4, z: 520}]}
    focus={[{...H, w: 0.15}, {...H, w: 0.09}]}><Clip name="project" locale={locale} from={7.4}/></Lens>
  </Shot>
  {/* 6 · Clientes tab: one album per student, faces and photo counts; long lateral move */}
  <Shot from={30.0} to={37.5}>
   <Lens W={W} H={HH} cam={[{px: CLIENT_ROWS.x0, py: CLIENT_ROWS.y0, rx: 26, ry: -25, z: 380}, {px: CLIENT_ROWS.x1, py: CLIENT_ROWS.y1, rx: 22, ry: -13, z: 420}]}
    focus={[{a: 172, c: 0.36, w: 0.09, f: 0.2}, {a: 172, c: 0.62, w: 0.09, f: 0.2}]}><Clip name="project" locale={locale} from={16.4}/></Lens>
  </Shot>
  {/* 7 · macro on "Enviar link": the cursor presses it and the send dialog opens (never submitted) */}
  <Shot from={37.0} to={41.6}>
   <Lens W={W} H={HH} cam={[{px: SEND_ROW.x, py: SEND_ROW.y, rx: 17, ry: -26, z: 700}, {px: SEND_ROW.x, py: SEND_ROW.y, rx: 12, ry: -15, z: 860}]}
    focus={[{cx: 0.5, cy: 0.5, r: 0.11, f: 0.2}, {cx: 0.5, cy: 0.5, r: 0.13, f: 0.2}]} blur={11}><Clip name="project" locale={locale} from={23.3}/></Lens>
  </Shot>
  {/* 8 · delivery: the student's own page, "Olá, Diego Almeida!" and his photos */}
  <Shot from={41.1} to={47.4}>
   <Lens W={W} H={HH} cam={[{px: PUBLIC_TITLE.x, py: PUBLIC_TITLE.y + 60, rx: 28, ry: 22, z: 120}, {px: PUBLIC_TITLE.x + 260, py: PUBLIC_TITLE.y + 260, rx: 20, ry: 12, z: 300}]}
    focus={[{...H, w: 0.13}, {...H, w: 0.1}]}><Clip name="extra" locale={locale} from={13.3}/></Lens>
  </Shot>
  {/* 9 · closing line, then the brand mark */}
  <Shot from={47.0} to={DURATION} out={0}>
   <Line a={0.4} b={2.9} parts={[[s.line2[0], C.ink], [s.line2[1], C.accentHi]]}/>
   <Mark a={2.9}/>
  </Shot>
 </Fonts>
 <Post/>
 <Music/>
 </AbsoluteFill>;
};

const MUSIC = 'audio/groupfaces-demo-music.wav';
const Music: React.FC = () => <Audio src={staticFile(MUSIC)} volume={f => interpolate(f, [0, FPS * 0.8, FPS * (DURATION - 2.5), FPS * DURATION - 1], [0, 1, 1, 0], {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'})}/>;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
